import * as THREE from 'three';
import type { World } from '../../sim/world';
import type { SimEvent } from '../../sim/events';
import type { Vessel } from '../../sim/vessel';
import { ParticleSystem, PKind } from './particles';
import { Rain } from './rain';
import { LightningBolts } from './lightning';
import type { FoamSim } from '../ocean/foam';
import { LAYER_WATER } from '../pipeline';
import type { ModelEvent } from '../ship/shipModel';
import { deckHalfWidth, deckY, halfBreadthXY, DECK_X_MAX, DECK_X_MIN } from '../../data/ship';
import { Vec3 } from '../../core/math';

const rnd = (a: number, b: number) => a + Math.random() * (b - a);

const WOOD: readonly number[][] = [[0.42, 0.3, 0.2], [0.3, 0.2, 0.13], [0.55, 0.42, 0.28], [0.05, 0.045, 0.04]];
const CANVAS_TINT = [0.85, 0.8, 0.7];
const DUST = [0.55, 0.48, 0.4];

interface FlashLight {
  light: THREE.PointLight;
  t: number;
  peak: number;
  decay: number;
}

/** Turns sim events and conditions into spray, smoke, fire, flashes, rain and bolts. */
export class Effects {
  readonly spray = new ParticleSystem(9000, false);
  readonly glow = new ParticleSystem(2600, true);
  readonly rain = new Rain();
  readonly bolts = new LightningBolts();
  private readonly flashes: FlashLight[] = [];
  private readonly fireLights: THREE.PointLight[] = [];
  private bowAcc = 0;
  private spumeAcc = 0;
  private rainAcc = 0;
  private mistAcc = 0;
  private fireAcc = new Map<string, number>();
  private wakeTimer = 0;
  /**
   * Is a ship in frame? Flames, sparks and spray that would come and go
   * unseen aren't made for ships out of it; smoke and foam, which linger
   * and drift into view, still are.
   */
  inFrame: (v: Vessel) => boolean = () => true;
  private time = 0;
  /** Camera shake requested this frame. */
  shake = 0;

  constructor(scene: THREE.Scene, private readonly world: World, private readonly foam: FoamSim, private readonly ship: THREE.Object3D) {
    for (const o of [this.spray.mesh, this.glow.mesh, this.rain.mesh, this.bolts.mesh]) {
      o.layers.set(LAYER_WATER);
      scene.add(o);
    }
    // A fixed pool of lights (changing the light count recompiles shaders).
    for (let i = 0; i < 3; i++) {
      const l = new THREE.PointLight(new THREE.Color(1, 0.6, 0.25), 0, 90, 2);
      l.layers.enableAll();
      scene.add(l);
      this.flashes.push({ light: l, t: 10, peak: 0, decay: 20 });
    }
    for (let i = 0; i < 2; i++) {
      const l = new THREE.PointLight(new THREE.Color(1, 0.45, 0.15), 0, 40, 2);
      l.layers.enableAll();
      scene.add(l);
      this.fireLights.push(l);
    }
  }

  setDepth(depth: THREE.Texture, w: number, h: number, near: number, far: number): void {
    this.spray.setDepth(depth, w, h, near, far);
    this.glow.setDepth(depth, w, h, near, far);
  }

  private local(x: number, y: number, z: number): THREE.Vector3 {
    return new THREE.Vector3(x, y, z).applyMatrix4(this.ship.matrixWorld);
  }

  private vessel(id: number): Vessel | undefined {
    return this.world.vessels.find((v) => v.id === id);
  }

  private flash(x: number, y: number, z: number, peak: number, decay: number, color?: THREE.Color): void {
    const f = this.flashes.reduce((a, b) => (a.t * a.decay > b.t * b.decay ? a : b));
    f.light.position.set(x, y, z);
    f.light.color.copy(color ?? new THREE.Color(1, 0.6, 0.25));
    f.light.distance = 40 + Math.sqrt(peak) * 3;
    f.t = 0;
    f.peak = peak;
    f.decay = decay;
  }

  /** Oak splinters and dust bursting from a hit. */
  private splinters(x: number, y: number, z: number, nx: number, nz: number, energy: number, sv: { x: number; z: number }, count: number): void {
    const n = Math.round(count * Math.min(2.5, 0.4 + energy));
    for (let i = 0; i < n; i++) {
      const s = rnd(4, 18) * Math.min(1.5, 0.5 + energy);
      const t = WOOD[Math.floor(Math.random() * WOOD.length)]!;
      this.spray.spawn(PKind.Debris, x, y, z, sv.x + nx * s + rnd(-5, 5), rnd(1, 10), sv.z + nz * s + rnd(-5, 5), rnd(1.2, 3), rnd(0.08, 0.35), rnd(0.08, 0.35), 1, t);
    }
    for (let i = 0; i < 6 + energy * 6; i++) {
      this.spray.spawn(PKind.Smoke, x + rnd(-0.4, 0.4), y + rnd(-0.3, 0.4), z + rnd(-0.4, 0.4), sv.x + nx * rnd(1, 4), rnd(0.2, 1.5), sv.z + nz * rnd(1, 4), rnd(2, 4), rnd(0.5, 1), rnd(2, 4), 0.5, DUST);
    }
  }

  /**
   * The cloud of dust and splinters a ball throws out of oak: a few large,
   * slow puffs that hang a second or two, so a hit is seen for what it is
   * from across the water (the splinters themselves are too small to see
   * past a cable or so). Bigger the further off it is seen from.
   */
  private hitPuff(x: number, y: number, z: number, nx: number, nz: number, energy: number, sv: { x: number; z: number }, dist: number): void {
    const far = Math.min(2.2, Math.max(1, dist / 220));
    const n = 3 + Math.round(Math.min(3, energy * 2));
    for (let i = 0; i < n; i++) {
      const s = rnd(1.4, 2.6) * far * Math.min(1.4, 0.6 + energy * 0.5);
      this.spray.spawn(PKind.Smoke, x + nx * rnd(0.2, 1), y + rnd(-0.2, 0.6), z + nz * rnd(0.2, 1),
        sv.x + nx * rnd(1.5, 4), rnd(0.4, 1.4), sv.z + nz * rnd(1.5, 4), rnd(1.6, 2.6), s * 0.5, s, 0.62, DUST);
    }
  }

  handle(events: SimEvent[], camera: THREE.Camera, modelEvents: ModelEvent[] = []): void {
    const cam = camera.position;
    const psv = this.world.ship.vel;
    for (const e of events) {
      switch (e.type) {
        case 'slam': {
          const n = Math.round(50 + e.intensity * 140);
          const out = new THREE.Vector3(e.x - this.ship.position.x, 0, e.z - this.ship.position.z).normalize();
          for (let i = 0; i < n; i++) {
            const up = rnd(3, 7 + e.intensity * 6);
            const o = rnd(1.5, 4 + e.intensity * 3);
            this.spray.spawn(PKind.Spray, e.x + rnd(-1.5, 1.5), e.y + rnd(0, 0.6), e.z + rnd(-1.5, 1.5),
              psv.x + out.x * o + rnd(-1, 1), up, psv.z + out.z * o + rnd(-1, 1), rnd(1.1, 2.4), rnd(0.35, 0.8), rnd(1.4, 3.2), 0.85);
          }
          for (let i = 0; i < 5 + e.intensity * 8; i++) {
            this.spray.spawn(PKind.Mist, e.x + rnd(-2, 2), e.y + rnd(0.5, 3), e.z + rnd(-2, 2), psv.x + out.x * 2, rnd(1, 3), psv.z + out.z * 2, rnd(2, 4), 2, rnd(5, 10), 0.35 * e.intensity);
          }
          this.foam.splash(e.x, e.z, 4 + e.intensity * 5, 0.9);
          if (cam.distanceTo(new THREE.Vector3(e.x, e.y, e.z)) < 40) this.shake += 0.12 * e.intensity;
          break;
        }
        case 'washOver': {
          const inward = new THREE.Vector3(0, 0, -e.side).transformDirection(this.ship.matrixWorld);
          for (let i = 0; i < 90 * e.intensity + 20; i++) {
            const s = rnd(2, 5);
            this.spray.spawn(PKind.Spray, e.x + rnd(-2.5, 2.5), e.y + rnd(0, 0.8), e.z + rnd(-0.5, 0.5),
              psv.x + inward.x * s, rnd(1, 4.5), psv.z + inward.z * s, rnd(0.8, 1.6), rnd(0.3, 0.6), rnd(1.0, 2.2), 0.8);
          }
          this.foam.splash(e.x, e.z, 6, 0.8);
          break;
        }
        case 'cannonFire': {
          const sv = this.vessel(e.vessel)?.ship.vel ?? psv;
          const d = new THREE.Vector3(e.dx, e.dy, e.dz);
          const f = this.glow;
          const dist = cam.distanceTo(new THREE.Vector3(e.x, e.y, e.z));
          const big = Math.cbrt(e.weight);
          f.spawn(PKind.Flash, e.x + d.x * 0.8, e.y + d.y * 0.8, e.z + d.z * 0.8, sv.x + d.x * 8, 0, sv.z + d.z * 8, 0.12, 1.2 * big, 3.2 * big, 1);
          for (let i = 0; i < 5; i++) {
            const t = rnd(0.5, 2.5);
            f.spawn(PKind.Flash, e.x + d.x * t, e.y + d.y * t + rnd(-0.2, 0.2), e.z + d.z * t, sv.x + d.x * 12, 0, sv.z + d.z * 12, rnd(0.08, 0.16), 0.8 * big, 2.0 * big, 0.7);
          }
          if (dist < 1500) {
            for (let i = 0; i < 28; i++) {
              const s = rnd(15, 55);
              f.spawn(PKind.Spark, e.x, e.y, e.z, sv.x + d.x * s + rnd(-6, 6), d.y * s + rnd(-2, 6), sv.z + d.z * s + rnd(-6, 6), rnd(0.3, 0.9), 0.08, 0.03, 0.8);
            }
          }
          // A dense bank of powder smoke rolling out of the port.
          const puffs = dist < 1200 ? 34 : dist < 4000 ? 14 : 5;
          for (let i = 0; i < puffs; i++) {
            const s = rnd(3, 30);
            const t = rnd(0.2, 4);
            this.spray.spawn(PKind.Smoke, e.x + d.x * t, e.y + d.y * t + rnd(-0.4, 0.5), e.z + d.z * t,
              sv.x + d.x * s + rnd(-1.5, 1.5), d.y * s + rnd(-0.3, 1.8), sv.z + d.z * s + rnd(-1.5, 1.5), rnd(9, 16), rnd(1.4, 2.6) * big, rnd(8, 15) * big, rnd(0.75, 0.95));
          }
          this.flash(e.x + d.x * 1.5, e.y, e.z + d.z * 1.5, 900 * big, 22);
          this.shake += Math.max(0, 0.45 - dist * 0.006);
          break;
        }
        case 'splash': {
          const k = e.size;
          for (let i = 0; i < 80 * k; i++) {
            const a = rnd(0, Math.PI * 2);
            const r = rnd(0, 1.5);
            this.spray.spawn(PKind.Spray, e.x + Math.cos(a) * r * 0.5, e.y + 0.2, e.z + Math.sin(a) * r * 0.5,
              Math.cos(a) * r * 1.8, rnd(5, 17) * k, Math.sin(a) * r * 1.8, rnd(1.5, 3.2), rnd(0.3, 0.7), rnd(1.0, 2.6), 0.9);
          }
          for (let i = 0; i < 8 * k; i++) {
            this.spray.spawn(PKind.Mist, e.x + rnd(-2, 2), e.y + rnd(1, 6), e.z + rnd(-2, 2), 0, rnd(0.5, 2), 0, rnd(2.5, 4), 3, 9, 0.3);
          }
          this.foam.splash(e.x, e.z, 5 * k, 1);
          break;
        }
        case 'impact': {
          const v = this.vessel(e.vessel);
          const sv = v?.ship.vel ?? psv;
          // Outward from the ship's centreline at the point of impact.
          let nx = 0, nz = 0;
          if (v) {
            const c = v.ship.com;
            const l = Math.hypot(e.x - c.x, e.z - c.z) || 1;
            nx = (e.x - c.x) / l;
            nz = (e.z - c.z) / l;
          }
          const dist = cam.distanceTo(new THREE.Vector3(e.x, e.y, e.z));
          if (dist > 2500) break;
          switch (e.what) {
            case 'hull': case 'rail': case 'deck': case 'rudder':
              this.splinters(e.x, e.y, e.z, nx, nz, e.energy, sv, e.what === 'rail' ? 30 : 22);
              this.hitPuff(e.x, e.y, e.z, nx, nz, e.energy, sv, dist);
              if (e.what !== 'rail' && e.y < this.world.waves.heightAt(e.x, e.z, true) + 0.6) this.foam.splash(e.x, e.z, 2, 0.6);
              break;
            case 'mast':
              this.splinters(e.x, e.y, e.z, -e.nx, -e.nz, e.energy * 1.4, sv, 30);
              this.hitPuff(e.x, e.y, e.z, -e.nx, -e.nz, e.energy, sv, dist);
              break;
            case 'gun':
              this.splinters(e.x, e.y, e.z, nx, nz, e.energy, sv, 14);
              for (let i = 0; i < 24; i++) {
                this.glow.spawn(PKind.Spark, e.x, e.y, e.z, sv.x + rnd(-12, 12), rnd(0, 10), sv.z + rnd(-12, 12), rnd(0.2, 0.6), 0.07, 0.03, 0.9);
              }
              break;
            case 'sail':
              for (let i = 0; i < 6; i++) {
                this.spray.spawn(PKind.Debris, e.x, e.y, e.z, sv.x + rnd(-3, 3), rnd(-1, 2), sv.z + rnd(-3, 3), rnd(2, 5), rnd(0.15, 0.4), rnd(0.2, 0.5), 1, CANVAS_TINT);
              }
              break;
            case 'rigging':
              for (let i = 0; i < 4; i++) {
                this.spray.spawn(PKind.Debris, e.x, e.y, e.z, sv.x + rnd(-4, 4), rnd(0, 4), sv.z + rnd(-4, 4), rnd(1.5, 3), 0.06, 0.08, 1, WOOD[1]);
              }
              break;
            default:
              break;
          }
          if (dist < 30) this.shake += 0.08 * Math.min(1.5, e.energy);
          break;
        }
        case 'explosion': {
          this.explosion(e.x, e.y, e.z, e.size, cam, e.vessel);
          break;
        }
        case 'sailTorn': {
          // Canvas blown out of her: shreds on the wind.
          if (e.level < 2 || cam.distanceTo(new THREE.Vector3(e.x, e.y, e.z)) > 2500) break;
          const v = this.vessel(e.vessel);
          const sv = v?.ship.vel ?? psv;
          const wx = this.world.wind.dirX * this.world.wind.speed * 0.5, wz = this.world.wind.dirZ * this.world.wind.speed * 0.5;
          for (let i = 0; i < 26; i++) {
            this.spray.spawn(PKind.Debris, e.x + rnd(-2, 2), e.y + rnd(-2, 2), e.z + rnd(-2, 2), sv.x + wx + rnd(-3, 3), rnd(-1, 2.5), sv.z + wz + rnd(-3, 3), rnd(3, 6), rnd(0.25, 0.7), rnd(0.3, 0.9), 1, CANVAS_TINT);
          }
          break;
        }
        case 'collapse': {
          const v = this.world.vessels.find((o) => Math.hypot(o.ship.com.x - e.x, o.ship.com.z - e.z) < 40);
          const sv = v?.ship.vel ?? psv;
          this.splinters(e.x, e.y, e.z, 0, 0, e.size, sv, 30);
          for (let i = 0; i < 12 * e.size; i++) {
            this.spray.spawn(PKind.Smoke, e.x + rnd(-3, 3), e.y + rnd(0, 2), e.z + rnd(-3, 3), sv.x, rnd(0.5, 2), sv.z, rnd(3, 6), rnd(1, 2), rnd(4, 8), 0.55, DUST);
          }
          break;
        }
        case 'collision': {
          const sv = psv;
          this.splinters(e.x, e.y, e.z, 0, 0, e.energy * 3, sv, 40);
          this.foam.splash(e.x, e.z, 6 + e.energy * 8, 1);
          for (let i = 0; i < 60 * e.energy + 10; i++) {
            this.spray.spawn(PKind.Spray, e.x + rnd(-2, 2), e.y - 1, e.z + rnd(-2, 2), rnd(-3, 3), rnd(3, 9), rnd(-3, 3), rnd(1, 2.2), rnd(0.3, 0.7), rnd(1, 2.5), 0.8);
          }
          if (cam.distanceTo(new THREE.Vector3(e.x, e.y, e.z)) < 80) this.shake += 0.5 * e.energy;
          break;
        }
        case 'fire': {
          if (!e.started) break;
          for (let i = 0; i < 12; i++) {
            this.glow.spawn(PKind.Flame, e.x + rnd(-0.5, 0.5), e.y + rnd(0, 0.5), e.z + rnd(-0.5, 0.5), rnd(-0.5, 0.5), rnd(1, 3), rnd(-0.5, 0.5), rnd(0.4, 0.8), 0.4, 1.4, 0.8);
          }
          break;
        }
        case 'shipSinking': {
          this.foam.splash(e.x, e.z, 30, 1);
          break;
        }
        case 'shipSunk': {
          // The last of her goes under: a boil of air, foam and wreckage.
          this.foam.splash(e.x, e.z, 45, 1);
          for (let i = 0; i < 200; i++) {
            const a = rnd(0, Math.PI * 2), r = rnd(0, 18);
            this.spray.spawn(PKind.Spray, e.x + Math.cos(a) * r, this.world.waves.heightAt(e.x, e.z, true) + 0.2, e.z + Math.sin(a) * r, rnd(-1, 1), rnd(2, 9), rnd(-1, 1), rnd(1, 2.5), rnd(0.4, 1), rnd(1.5, 3), 0.85);
          }
          for (let i = 0; i < 30; i++) {
            this.spray.spawn(PKind.Mist, e.x + rnd(-15, 15), rnd(1, 6), e.z + rnd(-15, 15), 0, rnd(0.3, 1.5), 0, rnd(6, 12), 5, 16, 0.3);
          }
          break;
        }
        case 'signal': {
          // A gun fired to windward: smoke and a report, no shot.
          for (let i = 0; i < 12; i++) {
            this.spray.spawn(PKind.Smoke, e.x + rnd(-1, 1), e.y + rnd(0, 1), e.z + rnd(-1, 1), rnd(-2, 2), rnd(0, 1.5), rnd(-2, 2), rnd(8, 14), 1.4, 9, 0.8);
          }
          this.glow.spawn(PKind.Flash, e.x, e.y, e.z, 0, 0, 0, 0.12, 1, 3, 1);
          break;
        }
        case 'lightning': {
          this.bolts.strike(e.x, e.z, this.world.weather.params.cloudBase + 250, Math.floor(Math.random() * 1e9));
          break;
        }
        default:
          break;
      }
    }
    for (const m of modelEvents) {
      // A mast and its yards crashing into the sea alongside.
      const h = this.world.waves.heightAt(m.x, m.z, true);
      this.handle([{ type: 'splash', x: m.x, y: h, z: m.z, size: m.size, kind: 'wreck' }], camera);
    }
  }

  private explosion(x: number, y: number, z: number, size: number, cam: THREE.Vector3, vesselId: number): void {
    const v = vesselId >= 0 ? this.vessel(vesselId) : undefined;
    const sv = v?.ship.vel ?? { x: 0, y: 0, z: 0 };
    const k = Math.sqrt(size);
    const dist = cam.distanceTo(new THREE.Vector3(x, y, z));
    // Fireball: flame blooming outward and climbing.
    for (let i = 0; i < 40 * k + 10; i++) {
      const dir = new THREE.Vector3(rnd(-1, 1), rnd(-0.2, 1), rnd(-1, 1)).normalize();
      const s = rnd(4, 14) * k;
      this.glow.spawn(PKind.Flame, x, y, z, sv.x + dir.x * s, dir.y * s + 2, sv.z + dir.z * s, rnd(0.5, 1.2) * Math.min(2, k), rnd(1, 2.5) * k, rnd(3, 6) * k, 1, [30, 12, 3]);
    }
    this.glow.spawn(PKind.Flash, x, y, z, sv.x, 0, sv.z, 0.25, 4 * k, 14 * k, 1, [80, 50, 25]);
    for (let i = 0; i < 60 * k; i++) {
      const s = rnd(10, 40) * k;
      this.glow.spawn(PKind.Ember, x, y, z, sv.x + rnd(-1, 1) * s, rnd(0.2, 1) * s, sv.z + rnd(-1, 1) * s, rnd(1, 3.5), 0.12, 0.05, 1);
    }
    // Planking, gun-carriage and worse, flung in all directions.
    const shards = Math.round(40 * k);
    for (let i = 0; i < shards; i++) {
      const s = rnd(6, 30) * Math.min(2.2, k);
      const t = WOOD[Math.floor(Math.random() * WOOD.length)]!;
      this.spray.spawn(PKind.Debris, x, y, z, sv.x + rnd(-1, 1) * s, rnd(0.3, 1.2) * s, sv.z + rnd(-1, 1) * s, rnd(2, 5), rnd(0.15, 0.7), rnd(0.15, 0.7), 1, t);
    }
    // A column of black smoke that rolls up and away.
    for (let i = 0; i < 25 * k; i++) {
      this.spray.spawn(PKind.Soot, x + rnd(-2, 2) * k, y + rnd(0, 3) * k, z + rnd(-2, 2) * k, sv.x + rnd(-2, 2), rnd(2, 7) * k, sv.z + rnd(-2, 2), rnd(10, 22), rnd(3, 6) * k, rnd(12, 26) * k, 0.9);
    }
    const h = this.world.waves.heightAt(x, z, true);
    if (y < h + 6) {
      this.foam.splash(x, z, 10 * k, 1);
      for (let i = 0; i < 70 * k; i++) {
        const a = rnd(0, Math.PI * 2), r = rnd(0, 3 * k);
        this.spray.spawn(PKind.Spray, x + Math.cos(a) * r, h + 0.2, z + Math.sin(a) * r, Math.cos(a) * rnd(2, 8), rnd(6, 20) * Math.min(1.6, k), Math.sin(a) * rnd(2, 8), rnd(1.5, 3.5), rnd(0.5, 1), rnd(2, 4), 0.9);
      }
    }
    this.flash(x, y + 1, z, 5000 * size, 6, new THREE.Color(1, 0.55, 0.2));
    this.shake += Math.max(0, (1.2 * k) - dist * 0.004);
  }

  update(dt: number, camera: THREE.PerspectiveCamera, onShip: boolean): void {
    const w = this.world;
    const ship = w.ship;
    const wind = w.wind;
    const p = w.weather.params;
    const sv = ship.vel;
    const waves = w.waves;
    this.time += dt;

    // Bow spray: speed and plunging into seas.
    this.bowAcc += dt * ship.bowSpray * (10 + Math.max(0, ship.speed) * 18);
    while (this.bowAcc > 1) {
      this.bowAcc -= 1;
      const side = Math.random() < 0.5 ? -1 : 1;
      const lx = rnd(14.5, 17.6);
      const hb = Math.max(0.3, halfBreadthXY(lx, 0.2));
      const pos = this.local(lx, rnd(-0.1, 0.5), side * (hb + 0.1));
      const out = new THREE.Vector3(0.35, 0, side).normalize().transformDirection(this.ship.matrixWorld);
      const s = rnd(1.5, 3.5) + ship.bowSpray * 2;
      this.spray.spawn(PKind.Spray, pos.x, pos.y, pos.z, sv.x * 0.9 + out.x * s, rnd(1.0, 2.5 + ship.bowSpray * 4), sv.z * 0.9 + out.z * s, rnd(0.8, 1.6), rnd(0.25, 0.5), rnd(0.8, 1.9), 0.7);
    }

    // Spume torn off crests in a gale.
    const cam = camera.position;
    this.spumeAcc += dt * Math.max(0, wind.speed - 12) * 14;
    const hs = waves.significantHeight;
    while (this.spumeAcc > 1) {
      this.spumeAcc -= 1;
      const a = rnd(0, Math.PI * 2);
      const r = rnd(8, 140);
      const x = cam.x + Math.cos(a) * r, z = cam.z + Math.sin(a) * r;
      const h = waves.heightAt(x, z, true);
      if (h < hs * 0.2) continue;
      for (let i = 0; i < 6; i++) {
        this.spray.spawn(PKind.Spray, x + rnd(-1, 1), h + rnd(0, 0.4), z + rnd(-1, 1), wind.dirX * wind.speed * 0.7, rnd(0.8, 2.5), wind.dirZ * wind.speed * 0.7, rnd(0.8, 1.6), rnd(0.3, 0.6), rnd(1.5, 3.5), 0.5);
      }
    }

    // Rain splashing on deck and sea.
    this.rainAcc += dt * p.rain * 420;
    while (this.rainAcc > 1) {
      this.rainAcc -= 1;
      if (Math.random() < 0.55) {
        const lx = rnd(DECK_X_MIN + 1, DECK_X_MAX - 1);
        const hw = deckHalfWidth(lx);
        if (hw <= 0) continue;
        const pos = this.local(lx, deckY(lx) + 0.14, rnd(-hw, hw));
        this.spray.spawn(PKind.Droplet, pos.x, pos.y, pos.z, sv.x + rnd(-0.4, 0.4), rnd(0.8, 1.8), sv.z + rnd(-0.4, 0.4), 0.25, 0.03, 0.05, 0.6);
      } else {
        const a = rnd(0, Math.PI * 2), r = rnd(2, 25);
        const x = cam.x + Math.cos(a) * r, z = cam.z + Math.sin(a) * r;
        const h = waves.heightAt(x, z, true);
        this.spray.spawn(PKind.Droplet, x, h + 0.02, z, 0, rnd(0.6, 1.4), 0, 0.2, 0.03, 0.05, 0.5);
      }
    }

    // Low sea mist in fog and heavy weather.
    this.mistAcc += dt * (p.fog * 2.5 + Math.max(0, wind.speed - 15) * 0.3);
    while (this.mistAcc > 1) {
      this.mistAcc -= 1;
      const a = rnd(0, Math.PI * 2), r = rnd(15, 90);
      const x = cam.x + Math.cos(a) * r, z = cam.z + Math.sin(a) * r;
      this.spray.spawn(PKind.Mist, x, rnd(0.5, 4), z, wind.dirX * wind.speed, rnd(0, 0.3), wind.dirZ * wind.speed, rnd(8, 14), rnd(6, 10), rnd(12, 22), 0.1 + p.fog * 0.1);
    }

    this.shipEffects(dt, cam);

    const wh = (x: number, z: number) => waves.heightAt(x, z, true);
    this.spray.update(dt, wind.dirX, wind.dirZ, wind.speed, wh);
    this.glow.update(dt, wind.dirX, wind.dirZ, wind.speed * 0.2, wh);

    for (const f of this.flashes) {
      f.t += dt;
      f.light.intensity = f.t < 3 / f.decay ? f.peak * Math.exp(-f.t * f.decay) : 0;
    }
    this.rain.update(w.time, camera, p.rain, wind.dirX, wind.dirZ, wind.speed, new THREE.Vector3(sv.x, sv.y, sv.z), onShip);
    this.bolts.update(w.weather.flash, !!w.weather.lightning);
  }

  /** Fires, sinking, pumping and the wakes of the other ships. */
  private shipEffects(dt: number, cam: THREE.Vector3): void {
    const w = this.world;
    const tmp = { x: 0, y: 0, z: 0 };
    const toWorld = (v: Vessel, x: number, y: number, z: number) => {
      const p = v.ship.designToWorld(tmpD.set(x, y, z), tmpD);
      tmp.x = p.x;
      tmp.y = p.y;
      tmp.z = p.z;
      return tmp;
    };
    const fires: { x: number; y: number; z: number; heat: number; d: number }[] = [];
    this.wakeTimer -= dt;
    const wakes = this.wakeTimer <= 0;
    if (wakes) this.wakeTimer = 0.25;
    for (const v of w.vessels) {
      if (v.state === 'sunk') continue;
      const s = v.ship;
      const d = Math.hypot(s.com.x - cam.x, s.com.z - cam.z);
      const sv = s.vel;
      const seen = v === w.player || this.inFrame(v);
      // Fire: flame tongues on deck, smoke pouring from ports and hatches.
      if (d < 3000) {
        for (const c of v.fire.cells) {
          if (c.heat < 0.03) continue;
          const key = `${v.id}:${c.level}:${c.station}:${c.lane}`;
          const deckFire = c.level === 1;
          let acc = (this.fireAcc.get(key) ?? 0) + dt * c.heat * (d < 600 ? 40 : 10);
          while (acc > 1) {
            acc -= 1;
            const px = c.x + rnd(-1.4, 1.4), pz = c.z + rnd(-0.8, 0.8);
            const hw = deckHalfWidth(px);
            const py = deckFire ? deckY(px) + 0.1 : deckY(px) - 0.2;
            const p = toWorld(v, px, py, Math.max(-hw, Math.min(hw, pz)));
            if (seen && (deckFire || c.heat > 0.55)) {
              const h = deckFire ? 1 : 0.6;
              this.glow.spawn(PKind.Flame, p.x, p.y, p.z, sv.x + rnd(-0.4, 0.4), rnd(1.5, 3.5) * h, sv.z + rnd(-0.4, 0.4), rnd(0.5, 1.1), rnd(0.5, 1) * (0.6 + c.heat), rnd(1.4, 2.8) * (0.6 + c.heat), 0.9);
              if (Math.random() < 0.15) this.glow.spawn(PKind.Ember, p.x, p.y + 1, p.z, sv.x + rnd(-1, 1), rnd(2, 6), sv.z + rnd(-1, 1), rnd(1, 3), 0.08, 0.04, 1);
            }
            if (Math.random() < 0.1 + 0.15 * c.heat) {
              // Below-deck fires show as smoke boiling out of the waist.
              const sp = toWorld(v, px, deckY(px) + (deckFire ? 1.2 : 0.3), Math.sign(pz || 1) * Math.min(hw, Math.abs(pz) + (deckFire ? 0 : 0.8)));
              this.spray.spawn(PKind.Soot, sp.x, sp.y, sp.z, sv.x, rnd(1.5, 3), sv.z, rnd(6, 12), rnd(1, 1.8), rnd(5, 10) * (0.5 + c.heat), 0.45 * (0.3 + c.heat));
            }
          }
          this.fireAcc.set(key, acc);
          if (c.heat > 0.2) {
            const p = toWorld(v, c.x, deckY(c.x) + 1.2, c.z);
            fires.push({ x: p.x, y: p.y, z: p.z, heat: c.heat, d: Math.hypot(p.x - cam.x, p.z - cam.z) });
          }
        }
      }
      if (d > 1200) continue;
      // Pumps going: water gushing from the dales over the side.
      if (seen && v.crew.pumpers > 0.5 && s.waterMass > 2000) {
        for (const side of [1, -1]) {
          if (Math.random() > dt * 18 * Math.min(1, v.crew.pumpers / 4)) continue;
          const p = toWorld(v, -1.2, deckY(-1.2) + 0.6, side * (deckHalfWidth(-1.2) + 0.2));
          const out = s.designToWorld(tmpD.set(-1.2, deckY(-1.2), side * 10), tmpD);
          const ox = (out.x - p.x) / 10, oz = (out.z - p.z) / 10;
          this.spray.spawn(PKind.Spray, p.x, p.y, p.z, sv.x + ox * rnd(1.5, 3), rnd(-0.5, 0.8), sv.z + oz * rnd(1.5, 3), rnd(0.8, 1.4), 0.15, 0.4, 0.8);
        }
      }
      // Water pouring in through holes near the surface.
      if (wakes) {
        for (const h of v.damage.holes) {
          if (h.patched > 0.9) continue;
          const p = toWorld(v, h.x, h.y, h.z);
          const wh = w.waves.heightAt(p.x, p.z, true);
          if (Math.abs(p.y - wh) < 0.8) this.foam.splash(p.x, p.z, 1.5 + h.r * 3, 0.5);
        }
      }
      // Going down: air boiling up round her, spray where she rolls.
      if (v.state === 'sinking' && Math.random() < dt * 20) {
        const px = rnd(-15, 15), pz = rnd(-4, 4);
        const p = toWorld(v, px, 0, pz);
        const wh = w.waves.heightAt(p.x, p.z, true);
        if (seen) this.spray.spawn(PKind.Spray, p.x, wh + 0.1, p.z, sv.x + rnd(-1, 1), rnd(1.5, 5), sv.z + rnd(-1, 1), rnd(0.8, 1.6), rnd(0.3, 0.6), rnd(0.8, 1.8), 0.8);
        if (wakes) this.foam.splash(p.x, p.z, 5, 0.8);
      }
      // Wakes of other ships close enough to show in the foam around us.
      if (wakes && v !== w.player && d < 180 && s.speed > 0.8) {
        const st = toWorld(v, -17.5, 0, 0);
        this.foam.splash(st.x, st.z, 2.5 + s.speed * 0.4, Math.min(0.8, s.speed * 0.15));
        const bw = toWorld(v, 16.5, 0, 0);
        this.foam.splash(bw.x, bw.z, 2 + s.speed * 0.3, Math.min(0.7, s.speed * 0.12));
      }
    }
    // The biggest fires nearest the camera light up the smoke and sails.
    fires.sort((a, b) => b.heat / (1 + b.d * 0.01) - a.heat / (1 + a.d * 0.01));
    this.fireLights.forEach((l, i) => {
      const f = fires[i];
      if (!f || f.d > 800) {
        l.intensity = 0;
        return;
      }
      l.position.set(f.x, f.y, f.z);
      const flick = 0.75 + 0.25 * Math.sin(this.time * 17 + i * 3) * Math.sin(this.time * 9.3 + i);
      l.intensity = 180 * f.heat * flick;
    });
  }
}

const tmpD = new Vec3();
